import {
  createContext,
  type ReactNode,
  startTransition,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  personalizationAttributesSchema,
  type PersonalizationAttributes,
} from './component-personalization.schema';

interface ComponentPersonalizationContextType {
  attributes: PersonalizationAttributes | undefined;
}

const ComponentPersonalizationContext = createContext<ComponentPersonalizationContextType>(
  null as unknown as ComponentPersonalizationContextType
);

export const useComponentPersonalizationContext = () => {
  const contextValues = useContext(ComponentPersonalizationContext);
  if (contextValues === null) {
    throw new Error(
      'useComponentPersonalizationContext must be used within a ComponentPersonalizationProvider'
    );
  }

  return contextValues;
};

interface ComponentPersonalizationProviderProps {
  children?: ReactNode;
}
const ComponentPersonalizationProvider = ({ children }: ComponentPersonalizationProviderProps) => {
  const loadingAttempsRef = useRef(0);
  const [personalizationAttributes, setPersonalizationAttributes] =
    useState<PersonalizationAttributes>();

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    if (typeof personalizationAttributes !== 'undefined') {
      return;
    }

    if (loadingAttempsRef.current >= 10) {
      return;
    }

    const interval = setInterval(() => {
      if (loadingAttempsRef.current >= 10) {
        clearInterval(interval);
        return;
      }

      const personalizationJSON = localStorage.getItem('_6senseCompanyDetails');

      if (typeof personalizationJSON !== 'string') {
        return;
      }

      try {
        const personalizationData = JSON.parse(personalizationJSON);
        startTransition(() => {
          setPersonalizationAttributes(personalizationAttributesSchema.parse(personalizationData));
        });
        clearInterval(interval);
      } catch (error) {
        console.error('Error parsing personalization JSON:', error);
      }

      loadingAttempsRef.current++;
    }, 200);

    return () => {
      clearInterval(interval);
    };
  }, [personalizationAttributes]);

  return (
    <ComponentPersonalizationContext.Provider
      value={useMemo(
        () => ({
          attributes: personalizationAttributes,
        }),
        [personalizationAttributes]
      )}
    >
      {children}
    </ComponentPersonalizationContext.Provider>
  );
};

export default ComponentPersonalizationProvider;
